import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  { key: 'validate',  label: 'Validating URL' },
  { key: 'gsc_match', label: 'Connecting to Search Console' },
  { key: 'create',    label: 'Adding to your account' },
  { key: 'sync',      label: 'Pulling keywords' },
  { key: 'enrich',    label: 'Enriching with search volume' },
];

function DetectIcon({ state }) {
  if (state === 'checking') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: 'spin 0.8s linear infinite' }}>
        <circle cx="12" cy="12" r="9" stroke="#94a3b8" strokeWidth="2.5" fill="none" strokeDasharray="14 14" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === 'matched') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#10b981" />
        <path d="M7 12.5l3.5 3.5L17 9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (state === 'error' || state === 'duplicate') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#ef4444" />
        <path d="M9 9l6 6M15 9l-6 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === 'not_in_gsc') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#f59e0b" />
        <path d="M12 7v6M12 16v.5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  return null;
}

function StepRow({ step, state, message }) {
  const isRunning = state === 'running';
  const isDone = state === 'done';
  const isWarning = state === 'warning';
  const isError = state === 'error';
  const isPending = !state;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', opacity: isPending ? 0.4 : 1, transition: 'opacity 0.3s' }}>
      <div style={{ width: 22, height: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isRunning && <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid #e5e7eb', borderTopColor: '#6366f1', animation: 'spin 0.7s linear infinite' }} />}
        {isDone && (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#10b981" />
            <path d="M7 12.5l3.5 3.5L17 9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {isWarning && (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#f59e0b" />
            <path d="M12 7v6M12 16v.5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {isError && (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#ef4444" />
            <path d="M9 9l6 6M15 9l-6 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {isPending && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#cbd5e1' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: isPending ? '#94a3b8' : '#1e293b' }}>{step.label}</div>
      </div>
      {message && !isPending && (
        <div style={{
          fontSize: 12,
          color: isDone ? '#10b981' : isWarning ? '#f59e0b' : isError ? '#ef4444' : '#64748b',
          fontWeight: 500, maxWidth: 200, textAlign: 'right',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{message}</div>
      )}
    </div>
  );
}

function VerifyInGsc({ url, hostname, onVerified, onBack }) {
  const [dnsHelp, setDnsHelp] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [pollState, setPollState] = useState('waiting');
  const [openedGsc, setOpenedGsc] = useState(false);

  useEffect(() => {
    const fetchHelp = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
        const res = await fetch(`/keyword-intelligence/api/v1/websites/dns-help?url=${encodeURIComponent(url)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setDnsHelp(data);
      } catch (e) { console.error('DNS help error:', e); }
    };
    fetchHelp();
  }, [url]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
        const res = await fetch(`/keyword-intelligence/api/v1/websites/detect?url=${encodeURIComponent(url)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        setPollCount(c => c + 1);
        if (data.matched && !data.already_added) {
          setPollState('found');
          setTimeout(() => onVerified(), 1200);
        }
      } catch (e) { console.warn('Poll error:', e); }
    };
    const interval = setInterval(poll, 10000);
    const initialTimeout = setTimeout(poll, 5000);
    return () => { cancelled = true; clearInterval(interval); clearTimeout(initialTimeout); };
  }, [url, onVerified]);

  const openGsc = () => {
    window.open(dnsHelp?.gsc_add_url || 'https://search.google.com/search-console/welcome', '_blank', 'noopener,noreferrer');
    setOpenedGsc(true);
  };

  const provider = dnsHelp?.provider;
  const providerKnown = provider && provider.id !== 'unknown' && provider.dashboard_url;

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <button onClick={onBack}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
          ← Back
        </button>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Verify {hostname} in Search Console
        </h1>
        <p style={{ fontSize: 15, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
          To track your keywords, Google needs to confirm you own this domain. This takes about 2 minutes.
        </p>
      </div>

      <div style={{
        padding: '14px 16px', borderRadius: 12, marginBottom: 20,
        background: pollState === 'found' ? '#f0fdf4' : '#eff6ff',
        border: `1.5px solid ${pollState === 'found' ? '#bbf7d0' : '#bfdbfe'}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        {pollState === 'found' ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#10b981" />
            <path d="M7 12.5l3.5 3.5L17 9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2.5px solid #bfdbfe', borderTopColor: '#3b82f6', animation: 'spin 0.9s linear infinite' }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: pollState === 'found' ? '#166534' : '#1e40af' }}>
            {pollState === 'found' ? 'Verification detected — moving on...' : 'Watching for verification'}
          </div>
          <div style={{ fontSize: 12, color: pollState === 'found' ? '#16a34a' : '#64748b', marginTop: 2 }}>
            {pollState === 'found' ? 'Your domain is now verified.' : `We will detect it the moment Google confirms${pollCount > 0 ? ` · checked ${pollCount}x` : ''}`}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: '20px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: openedGsc ? '#10b981' : '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
            {openedGsc ? '✓' : '1'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Open Google Search Console</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Click below, sign in with your Google account, then click <strong>+ Add property</strong>. Choose <strong>Domain</strong> and enter <strong>{hostname}</strong>.
            </div>
            <button onClick={openGsc}
              style={{
                padding: '10px 18px', fontSize: 13, fontWeight: 600,
                background: openedGsc ? '#f1f5f9' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: openedGsc ? '#475569' : '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer',
              }}>
              {openedGsc ? '✓ Reopen Search Console' : 'Open Search Console →'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: '20px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>2</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Add the verification record to your DNS</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Google will give you a value like <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>google-site-verification=...</code>. Add it as a TXT record in your DNS.
            </div>

            {providerKnown && (
              <div style={{ padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 6 }}>
                  We detected your DNS is managed by <strong>{provider.name}</strong>
                </div>
                <a href={provider.dashboard_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, textDecoration: 'none' }}>
                  Open {provider.name} dashboard →
                </a>
              </div>
            )}
            {!providerKnown && provider?.nameservers && (
              <div style={{ padding: '12px 14px', background: '#fffbeb', border: '1px solid #fed7aa', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
                  Could not auto-detect your DNS provider
                </div>
                <div style={{ fontSize: 11, color: '#a16207', fontFamily: 'monospace' }}>
                  Your nameservers: {provider.nameservers.join(', ')}
                </div>
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
                  Log in wherever you manage these nameservers and add a TXT record at the root.
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <div><strong>Type:</strong> TXT</div>
              <div><strong>Name/Host:</strong> @ or leave blank</div>
              <div><strong>Value:</strong> <em>(paste the value Google gives you)</em></div>
              <div><strong>TTL:</strong> Auto or 3600</div>
            </div>
          </div>
        </div>
      </div>

      <details style={{ marginBottom: 16 }}>
        <summary style={{ fontSize: 13, color: '#6366f1', cursor: 'pointer', padding: '8px 0', fontWeight: 500 }}>
          Cannot edit DNS? Use the HTML file method instead
        </summary>
        <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, marginTop: 8, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
          In Search Console, choose <strong>"URL prefix"</strong> property type instead. Google will give you an HTML file to download. Upload it to the root of your website (so it is reachable at <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>https://{hostname}/google[hash].html</code>). Then click Verify.
        </div>
      </details>

      <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>
        DNS changes can take 1–5 minutes to propagate. We are checking automatically.
      </div>
    </>
  );
}

function AddSitePage({ onClose, onAdded }) {
  const [url, setUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [detectState, setDetectState] = useState(null);
  const [detectInfo, setDetectInfo] = useState(null);
  const [phase, setPhase] = useState('input');
  const [stepStates, setStepStates] = useState({});
  const [completedWebsiteId, setCompletedWebsiteId] = useState(null);
  const [globalError, setGlobalError] = useState(null);

  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!url || url.length < 4) {
      setDetectState(null);
      setDetectInfo(null);
      return;
    }
    clearTimeout(debounceRef.current);
    setDetectState('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
        const res = await fetch(`/keyword-intelligence/api/v1/websites/detect?url=${encodeURIComponent(url)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setDetectInfo(data);
        if (data.already_added) setDetectState('duplicate');
        else if (data.matched) {
          setDetectState('matched');
          if (!displayName && data.hostname) {
            const suggested = data.hostname.split('.')[0];
            setDisplayName(suggested.charAt(0).toUpperCase() + suggested.slice(1));
          }
        } else if (data.reason === 'gsc_not_connected') setDetectState('error');
        else setDetectState('not_in_gsc');
      } catch (e) {
        setDetectState('error');
        setDetectInfo({ error: e.message });
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [url]);

  const startAdding = useCallback(async () => {
    setPhase('progress');
    setStepStates({});
    setGlobalError(null);
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';

    try {
      const res = await fetch('/keyword-intelligence/api/v1/websites/quick-add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ url, display_name: displayName }),
      });
      if (!res.ok) {
        const errText = await res.text();
        setGlobalError(`Server error: ${errText}`);
        setPhase('failed');
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const block of lines) {
          if (!block.trim()) continue;
          const dataLine = block.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const event = JSON.parse(dataLine.slice(6));
            if (event.type === 'progress') {
              setStepStates(prev => ({ ...prev, [event.step]: { state: event.status, message: event.message } }));
            } else if (event.type === 'done') {
              setCompletedWebsiteId(event.website_id);
              setPhase('done');
            } else if (event.type === 'error') {
              setGlobalError(event.message);
              setStepStates(prev => ({ ...prev, [event.step || 'unknown']: { state: 'error', message: event.message } }));
              setPhase('failed');
            }
          } catch (err) { console.warn('SSE parse error:', err); }
        }
      }
    } catch (e) {
      setGlobalError(e.message);
      setPhase('failed');
    }
  }, [url, displayName]);

  const finishAndGo = () => {
    if (onAdded && completedWebsiteId) onAdded(completedWebsiteId);
    else if (onClose) onClose();
  };

  const handleVerified = useCallback(() => {
    setPhase('input');
    setDetectState('matched');
    setTimeout(() => startAdding(), 600);
  }, [startAdding]);

  const canSubmit = detectState === 'matched' && url && displayName;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 2000, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
          {phase === 'input' && 'Add a site'}
          {phase === 'verify' && 'Verify domain'}
          {phase === 'progress' && 'Setting up...'}
          {phase === 'done' && 'All set'}
          {phase === 'failed' && 'Something went wrong'}
        </div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', padding: 4, lineHeight: 1 }}>×</button>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: phase === 'verify' ? 'flex-start' : 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 580 }}>

          {phase === 'input' && (
            <>
              <h1 style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Add a site</h1>
              <p style={{ fontSize: 15, color: '#64748b', margin: '0 0 32px', lineHeight: 1.5 }}>
                Type your domain. We will find it in Search Console and pull your keyword data automatically.
              </p>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 8 }}>Website URL</label>
                <div style={{ position: 'relative' }}>
                  <input ref={inputRef} type="text" value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="example.com"
                    style={{
                      width: '100%', padding: '14px 44px 14px 16px', fontSize: 16,
                      border: '2px solid',
                      borderColor: detectState === 'matched' ? '#10b981' :
                                   detectState === 'duplicate' || detectState === 'error' ? '#ef4444' :
                                   detectState === 'not_in_gsc' ? '#f59e0b' : '#e2e8f0',
                      borderRadius: 12, outline: 'none', boxSizing: 'border-box',
                      transition: 'border-color 0.2s', fontFamily: 'inherit',
                    }} />
                  {detectState && (
                    <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>
                      <DetectIcon state={detectState === 'duplicate' ? 'error' : detectState} />
                    </div>
                  )}
                </div>

                {detectState === 'matched' && detectInfo && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>✓ Found in Search Console</span>
                    <span style={{ color: '#86efac' }}>·</span>
                    <span style={{ color: '#16a34a', fontFamily: 'monospace', fontSize: 12 }}>
                      {detectInfo.match_type === 'sc-domain' ? 'Domain property' : 'URL-prefix'}
                    </span>
                  </div>
                )}
                {detectState === 'duplicate' && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
                    This site is already in your account.
                  </div>
                )}
                {detectState === 'not_in_gsc' && (
                  <div style={{ marginTop: 10, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Not yet in Search Console</div>
                    <div style={{ marginBottom: 10, lineHeight: 1.5 }}>
                      We will walk you through verifying this domain — takes about 2 minutes.
                    </div>
                    <button onClick={() => setPhase('verify')}
                      style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                      Verify {detectInfo?.hostname} →
                    </button>
                  </div>
                )}
                {detectState === 'error' && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
                    {detectInfo?.reason === 'gsc_not_connected'
                      ? 'Search Console is not connected. Please reconnect from Settings first.'
                      : 'Could not validate this URL. Please try again.'}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 32 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 8 }}>Display name</label>
                <input type="text" value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Auto-generated from URL"
                  style={{
                    width: '100%', padding: '14px 16px', fontSize: 16,
                    border: '2px solid #e2e8f0', borderRadius: 12, outline: 'none',
                    boxSizing: 'border-box', fontFamily: 'inherit',
                  }} />
              </div>

              <button onClick={startAdding} disabled={!canSubmit}
                style={{
                  width: '100%', padding: '14px 20px', fontSize: 15, fontWeight: 600,
                  background: canSubmit ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : '#e2e8f0',
                  color: canSubmit ? '#fff' : '#94a3b8',
                  border: 'none', borderRadius: 12,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.1s', boxShadow: canSubmit ? '0 4px 12px rgba(99, 102, 241, 0.25)' : 'none',
                }}>
                Continue →
              </button>

              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 16, textAlign: 'center' }}>
                Setup takes about 60 seconds. You will see live progress.
              </p>
            </>
          )}

          {phase === 'verify' && (
            <VerifyInGsc url={url} hostname={detectInfo?.hostname || url}
              onVerified={handleVerified} onBack={() => setPhase('input')} />
          )}

          {(phase === 'progress' || phase === 'done' || phase === 'failed') && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
                {phase === 'done' ? '🎉 All set' : phase === 'failed' ? '⚠️ Setup failed' : 'Setting up'}
              </h1>
              <p style={{ fontSize: 15, color: '#64748b', margin: '0 0 28px' }}>{url}</p>

              <div style={{ background: '#f8fafc', borderRadius: 14, padding: '8px 20px', border: '1px solid #f1f5f9' }}>
                {STEPS.map((step, i) => {
                  const s = stepStates[step.key] || {};
                  return (
                    <React.Fragment key={step.key}>
                      <StepRow step={step} state={s.state} message={s.message} />
                      {i < STEPS.length - 1 && <div style={{ height: 1, background: '#e2e8f0', marginLeft: 36 }} />}
                    </React.Fragment>
                  );
                })}
              </div>

              {globalError && phase === 'failed' && (
                <div style={{ marginTop: 20, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
                  {globalError}
                </div>
              )}

              {phase === 'done' && (
                <button onClick={finishAndGo}
                  style={{
                    width: '100%', padding: '14px 20px', fontSize: 15, fontWeight: 600,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', marginTop: 24,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                  }}>
                  View your data →
                </button>
              )}

              {phase === 'failed' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                  <button onClick={() => { setPhase('input'); setStepStates({}); setGlobalError(null); }}
                    style={{ flex: 1, padding: '14px 20px', fontSize: 15, fontWeight: 600, background: '#fff', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: 12, cursor: 'pointer' }}>
                    Try again
                  </button>
                  <button onClick={onClose}
                    style={{ flex: 1, padding: '14px 20px', fontSize: 15, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, cursor: 'pointer' }}>
                    Close
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default AddSitePage;
